import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { AuthenticatedMobileShell } from '../components/AuthenticatedMobileShell.tsx';
import { InvoiceEditor } from '../components/InvoiceEditor.tsx';
import { StatusMessage } from '../components/StatusMessage.tsx';
import { useMe } from '../lib/auth.ts';
import { useCounterparties } from '../lib/counterparties.ts';
import { useDeleteInvoice, useInvoice, useUpdateInvoice } from '../lib/invoices.ts';
import { useSettings } from '../lib/settings.ts';
import type { InvoiceDetailResponse, UpsertInvoiceInput } from '../../shared/invoice.ts';
import { CenteredMessage, LoginScreen } from './counterparties.tsx';

export const Route = createFileRoute('/invoices/$id')({
  validateSearch: (search: Record<string, unknown>): { saved?: number } => {
    const raw = search.saved;
    const saved = typeof raw === 'string' || typeof raw === 'number' ? Number(raw) : undefined;
    return typeof saved === 'number' && Number.isInteger(saved) && saved > 0 ? { saved } : {};
  },
  component: InvoiceDetailRoute,
});

export function InvoiceDetailRoute(): React.ReactElement {
  const me = useMe();

  if (me.isLoading) {
    return <CenteredMessage>Loading…</CenteredMessage>;
  }

  if (me.data == null) {
    return <LoginScreen />;
  }

  return (
    <AuthenticatedMobileShell currentPath="/invoices/detail">
      <AuthenticatedInvoiceDetail />
    </AuthenticatedMobileShell>
  );
}

function AuthenticatedInvoiceDetail(): React.ReactElement {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const invoice = useInvoice(id);
  const settings = useSettings();
  const customers = useCounterparties('');
  const updateInvoice = useUpdateInvoice();
  const deleteInvoice = useDeleteInvoice();

  if (invoice.isPending || settings.isPending || customers.isPending) {
    return <InvoiceEditorShell title="Invoice">Loading…</InvoiceEditorShell>;
  }

  if (invoice.isError || settings.isError || customers.isError) {
    return (
      <InvoiceEditorShell title="Invoice">
        <StatusMessage tone="error">
          Could not load invoice. Try again or check the logs.
        </StatusMessage>
      </InvoiceEditorShell>
    );
  }

  async function save(input: UpsertInvoiceInput): Promise<InvoiceDetailResponse> {
    return updateInvoice.mutateAsync({ id, invoice: input });
  }

  async function saveAndClose(input: UpsertInvoiceInput): Promise<InvoiceDetailResponse> {
    const response = await updateInvoice.mutateAsync({ id, invoice: input });
    await navigate({ to: '/' as never });
    return response;
  }

  const onDelete = invoice.data.invoice.isArchive
    ? undefined
    : async (): Promise<void> => {
        await deleteInvoice.mutateAsync(id);
        await navigate({ to: '/' as never });
      };

  return (
    <InvoiceEditor
      mode="edit"
      settings={settings.data}
      customers={customers.data.counterparties}
      invoice={invoice.data.invoice}
      initialSaveMessage={search.saved ? `Invoice #${search.saved} saved.` : null}
      onSave={save}
      onSaveAndClose={saveAndClose}
      onDelete={onDelete}
    />
  );
}

function InvoiceEditorShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="min-h-screen">
      <header className="border-b border-line bg-paper-soft/60">
        <div className="mx-auto max-w-6xl px-4 py-5 sm:px-6">
          <h1 className="font-serif text-2xl italic text-ink">{title}</h1>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8 text-sm text-ink-soft sm:px-6">{children}</main>
    </div>
  );
}
